<template>
  <v-card class="overflow-y-auto">
    <v-card-title class="d-flex justify-space-between align-center">
      <div>Calendar Block</div>
      <div v-if="!isMultiUnit">
        <reservation-create-modal
          class="ml-auto"
          :pre-filled="preFilled"
          @oncreate="createReservation"
        />
      </div>
    </v-card-title>
    <v-form ref="form" @submit.prevent="save">
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <multi-property-picker
              v-model="block.listings_ids"
              :rules="[required]"
              :custom-listings="listingsActiveAndDups"
              :disabled="isMultiUnit"
              hide-details
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" md="4">
            <date-picker
              v-model="block.from"
              label="from"
              :rules="[required]"
              :hide-details="true"
              :allowed-dates="dateIsAllowed"
              @input="onFormChanged($event, 'from')"
            />
          </v-col>
          <v-col cols="6" md="4">
            <date-picker
              v-model="block.to"
              :allowed-dates="dateIsAllowed"
              label="to"
              :hide-details="true"
              :rules="[required]"
              @input="onFormChanged($event, 'to')"
            />
          </v-col>
          <v-col cols="6" md="4">
            <v-text-field
              v-model.number="block.minNights"
              type="number"
              label="Min Nights"
              outlined
              :hide-details="true"
              dense
              :rules="[max(365), min(1)]"
              @input="onFormChanged($event, 'min_nights')"
            />
          </v-col>
          <v-col cols="6" md="4">
            <v-select
              v-model="block.status"
              label="Status"
              outlined
              dense
              :items="[
                { text: 'Blocked', value: 'unavailable' },
                { text: 'Available', value: 'available' },
              ]"
              :hide-details="true"
              @input="onFormChanged($event, 'status')"
            />
          </v-col>
          <v-col cols="6" md="4" class="d-flex align-center">
            <v-checkbox
              v-model="block.manualOverride"
              class="ma-0"
              hide-details
              :label="$t('Manual Override')"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-checkbox
              v-model="changePrice"
              class="ma-0"
              hide-details
              :label="$t('Change price')"
            />
          </v-col>
          <template v-if="changePrice">
            <v-col v-if="activePriceType === 'price'" cols="4">
              <v-text-field
                v-model.number="block.price"
                type="number"
                :hide-details="true"
                label="Static price"
                outlined
                dense
                :rules="changePrice ? [required] : []"
                @input="onFormChanged($event, 'price')"
              />
            </v-col>
          </template>
        </v-row>

        <v-textarea
          v-model="block.note"
          outlined
          dense
          label="Note"
          :rules="block.status === 'unavailable' ? [required] : []"
          @input="onFormChanged($event, 'note')"
        />

        <v-row
          v-if="
            hasLogs || isLoadingLogs || (!isLoadingLogs && logs.length === 0)
          "
        >
          <v-col cols="12">
            <div v-if="isLoadingLogs" class="d-flex align-center mb-2">
              <v-progress-circular
                indeterminate
                color="primary"
                size="16"
                width="2"
              ></v-progress-circular>
              <span class="ml-2 text-body-2 grey--text"
                >Loading history...</span
              >
            </div>
            <v-btn
              v-else-if="hasLogs"
              small
              text
              color="info"
              class="mb-2"
              @click="showHistoryDialog = true"
            >
              <v-icon small left>mdi-history</v-icon>
              View history for selected dates ({{ logs.length }})
            </v-btn>
            <div v-else class="text-body-2 grey--text mb-2">
              No history found for {{ formatDate(block.from) }} to
              {{ formatDate(block.to) }}
            </div>
          </v-col>
        </v-row>

        <!-- History Dialog -->
        <v-dialog v-model="showHistoryDialog" max-width="800px">
          <v-card elevation="8">
            <v-card-title class="primary white--text">
              <v-icon left color="white">mdi-history</v-icon>
              Calendar Block History
              <v-spacer></v-spacer>
              <v-btn
                icon
                small
                color="white"
                @click="showHistoryDialog = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-4">
              <div>
                <div
                  v-for="log in sortedLogs"
                  :key="log.json_path"
                  class="mb-4"
                >
                  <v-subheader class="font-weight-medium primary--text px-0">
                    <v-icon small left color="primary">mdi-calendar</v-icon>
                    {{ formatDate(log.json_path) }}
                  </v-subheader>
                  <v-card outlined class="ml-4">
                    <div
                      v-for="actionItem in getSortedActions(log.actions)"
                      :key="actionItem.timestamp"
                      class="action-item pa-3"
                    >
                      <div class="d-flex align-start">
                        <v-icon small color="success" class="mt-1"
                          >mdi-circle-small</v-icon
                        >
                        <div class="flex-grow-1 ml-3">
                          <div class="text-body-2 font-weight-medium mb-1">
                            {{ actionItem.action || 'Calendar change' }}
                          </div>
                          <div
                            class="text-caption grey--text d-flex justify-space-between"
                          >
                            <span>{{
                              actionItem.user_name || 'Unknown user'
                            }}</span>
                            <span>{{
                              formatTimestamp(actionItem.timestamp)
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-card>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn color="info" outlined text @click="close">Cancel</v-btn>
        <v-btn color="info" type="submit">Save</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import DatePicker from 'components/form-fields/date-picker'
import FormRules from 'components/mixins/form-rules-mixin'
import ReservationCreateModal from 'components/reservation/reservation-create-modal'
import MultiPropertyPicker from 'components/multi-property-picker.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'CalendarBlockModal',
  components: { ReservationCreateModal, DatePicker, MultiPropertyPicker },
  mixins: [FormRules],
  props: [
    'from',
    'to',
    'note',
    'listingId',
    'minNights',
    'status',
    'onChange',
    'onReservationCreate',
    'isMultiUnit',
    'rates',
  ],
  data() {
    return {
      block: {
        original_from: this.from,
        original_to: this.to,
        from: this.from,
        to: this.to,
        note: this.note,
        minNights: this.minNights,
        status: this.status,
        manualOverride: this.rates[this.from].manual_override,
        listings_ids: [this.listingId],
      },
      activePriceType: 'price',
      changePrice: false,
      preFilled: {
        range: [this.from, this.to],
        listingId: this.listingId,
      },
      hasUserModifications: false,
      logs: [],
      isLoadingLogs: false,
      showHistoryDialog: false,
    }
  },
  watch: {
    changePrice(newValue) {
      if (!newValue) this.block.price = null
    },
    activePriceType(newValue, oldValue) {
      if (newValue !== oldValue) this.block.price = null
    },
    'block.status'(newValue) {
      if (!this.hasUserModifications) {
        if (!this.block.minNights) {
          this.block.minNights = this.rates[this.from].minNights || 2
        }
        if (newValue === 'available') {
          this.block.manualOverride = false
          this.block.note = null
        } else if (newValue === 'unavailable' && !this.block.note) {
          this.block.note = this.$t('Block')
        }
      }
    },
    'block.from'() {
      this.fetchLogs()
    },
    'block.to'() {
      this.fetchLogs()
    },
  },
  computed: {
    ...mapGetters(['listingsActiveAndDups']),
    hasLogs() {
      return this.logs.length > 0
    },
    sortedLogs() {
      return this.logs.toSorted((a, b) => {
        const dateA = new Date(a.json_path).getTime()
        const dateB = new Date(b.json_path).getTime()
        return dateB - dateA
      })
    },
  },
  mounted() {
    this.fetchLogs()
  },
  methods: {
    close() {
      this.$store.commit('hideModal')
    },
    dateIsAllowed(date) {
      return (
        this.rates[date] &&
        this.rates[date].status !== 'booked' &&
        this.rates[date].block_type !== 'smart_rules'
      )
    },
    createReservation(res_id) {
      this.onReservationCreate(res_id)
      this.close()
    },
    async save() {
      if (this.$refs.form.validate()) {
        try {
          this.$store.commit('updateLoading', true)
          const rates = await this.$store.dispatch(
            'listingCalendar/blockCalendarBatch',
            {
              ...this.block,
            }
          )
          this.$store.commit('updateLoading', false)
          this.onChange(rates, this.block)
          this.close()
        } catch (error) {
          this.$store.commit('updateLoading', false)
          console.error('Failed to block calendar:', error)
        }
      }
    },
    onFormChanged(_val, key) {
      if (key === 'min_nights' || key === 'price') {
        this.block.manualOverride = true
        this.hasUserModifications = true
      }
    },
    async fetchLogs() {
      try {
        this.isLoadingLogs = true

        const { data } = await this.$store.dispatch('getManualActionsLog', {
          type: 'Listing',
          id: this.listingId,
          field: 'day_rates',
          json_range: [this.block.from, this.block.to],
        })

        this.logs = data.logs || []
        this.isLoadingLogs = false
      } catch (error) {
        console.error('Failed to fetch logs:', error)
        this.isLoadingLogs = false
      }
    },
    formatDate(date) {
      const formattedDate = new Date(date).toLocaleDateString()
      return formattedDate.split('/').reverse().join('-')
    },
    formatTimestamp(timestamp) {
      const formattedTimestamp = new Date(timestamp * 1000).toLocaleString()
      return formattedTimestamp.split(', ').join(' at ')
    },
    getSortedActions(actions) {
      return Object.entries(actions)
        .sort((a, b) => b[0] - a[0])
        .map(entry => ({
          action: entry[1].action,
          user_name: entry[1].user_name,
          timestamp: entry[0],
        }))
    },
  },
}
</script>

<style scoped>
.action-item:not(:last-child) {
  border-bottom: 1px solid #f5f5f5;
}
</style>
